/* ============================================================
   OTAK DASHBOARD  —  useDashboard()
   ============================================================
   SEMUA logik halaman Dashboard duduk di sini: bacaan doc
   pengguna, wish harian, link group sokongan, senarai produk
   terbaru, dan tutorial kali pertama.

   ⛔ TIADA JSX dalam fail ini, dan JANGAN salin logik ini ke
      dalam fail rupa. Dua rupa berkongsi satu otak:

        GettingStartedView.jsx  — rupa lama (suis v3.9 mati)
        DashboardV39View.jsx    — rupa v3.9 (suis hidup)

      Kalau logik disalin, satu salinan akan terlepas pembetulan
      dan kegagalannya SENYAP — pelanggan tekan Simpan pada wish,
      tiada ralat, tetapi tiada apa yang tersimpan.

   ⚡ Kos: doc `users/{uid}` dibaca SEKALI sahaja di sini. Dahulu
      ia dibaca TIGA kali setiap kali Dashboard dibuka (wish,
      tutorial, referredBy — tiga useEffect berasingan pada doc
      yang sama). Jangan pecahkan semula.

   `enabled: false` — otak dimatikan sepenuhnya (tiada bacaan
   Firestore langsung). Dipakai oleh mod PELAN `/dashboard-v3.9`
   yang memapar data contoh; tanpa ini halaman prototaip
   membakar bacaan doc setiap kali dibuka.
   ============================================================ */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    collection, query, where, orderBy, limit, getDocs, doc, getDoc, setDoc,
} from 'firebase/firestore';

import { db, auth } from '../firebase';
import { getAffiliateBySlug, officialSupportGroup } from '../services/affiliateService';
import { getUserRole, ROLES } from '../utils/roles';

export const DEFAULT_WISH = 'Semoga dapat capai komisen 3-4 angka sehari!';

/** Ucapan ikut waktu tempatan pengguna. */
export function greetingNow() {
    const h = new Date().getHours();
    if (h < 12) return 'Selamat Pagi';
    if (h < 17) return 'Selamat Tengahari';
    return 'Selamat Malam';
}

/* Versi kunci kamus bagi ucapan yang sama. Rupa LAMA terus memakai
   greetingNow() (ayat Melayu tetap); rupa v3.9 memakai kunci ini dan
   menterjemahnya, supaya ucapan ikut suis bahasa halaman. Dua fungsi,
   satu peraturan waktu — pukul yang sama, jangan pisahkan. */
export function greetingKeyNow() {
    const h = new Date().getHours();
    if (h < 12) return 'dashboard.greeting.morning';
    if (h < 17) return 'dashboard.greeting.afternoon';
    return 'dashboard.greeting.evening';
}

/* ⛔ Seksyen "Produk Baru" pada Dashboard = ADMIN SAHAJA (arahan user
   2026-08-21). Satu takrifan, dipakai TIGA tempat — di sini (melangkau
   bacaan Firestore) dan dalam kedua-dua fail rupa (melangkau render):
   GettingStartedView.jsx + DashboardV39View.jsx.

   Kenapa DUA lapisan: melangkau bacaan sahaja bergantung pada andaian
   bahawa senarai kosong akan kekal memulangkan null; melangkau render
   sahaja tetap membayar 10 bacaan doc SETIAP kali mana-mana pengguna
   membuka Dashboard, untuk sesuatu yang tidak dipapar.

   ⚠️ Moderator TIDAK nampak juga — pagar sama seperti `isAdmin` dalam
      App.jsx (ROLES.ADMIN sahaja).
   ⛔ Jangan salin syarat ini ke dalam fail rupa; panggil fungsi ini. */
export function bolehLihatProdukBaru(user) {
    return getUserRole(user?.email || auth.currentUser?.email) === ROLES.ADMIN;
}

export function useDashboard(user, { isPaid = false, enabled = true } = {}) {
    /* ── Wish harian ────────────────────────────────────────── */
    const [wish, setWish] = useState(DEFAULT_WISH);
    const [isEditingWish, setIsEditingWish] = useState(false);
    const [wishDraft, setWishDraft] = useState('');
    const [savingWish, setSavingWish] = useState(false);
    const wishInputRef = useRef(null);

    /* ── Tutorial kali pertama ──────────────────────────────── */
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialIsTrial, setTutorialIsTrial] = useState(false);

    /* ── Group sokongan ─────────────────────────────────────── */
    // Link group ejen yang merujuk pengguna ini, kalau ejen itu ada set.
    const [affiliateGroupLink, setAffiliateGroupLink] = useState(null);

    /* ── Produk terbaru pustaka System ──────────────────────── */
    const [latestProducts, setLatestProducts] = useState([]);

    /* Produk Baru = admin sahaja — lihat nota pada bolehLihatProdukBaru(). */
    const showNewProducts = bolehLihatProdukBaru(user);

    /* ══ SATU bacaan doc pengguna untuk ketiga-tiga benda di atas ══ */
    useEffect(() => {
        if (!enabled) return undefined;
        const uid = auth.currentUser?.uid;
        if (!uid) return undefined;

        let cancelled = false;
        (async () => {
            let d = null;
            try {
                const snap = await getDoc(doc(db, 'users', uid));
                if (cancelled || !snap.exists()) return;
                d = snap.data() || {};
            } catch (_) {
                return; // gagal baca → wish default + tiada tutorial; halaman tetap hidup
            }

            if (d.dashboardWish) setWish(d.dashboardWish);

            // Tutorial — trial: sekali setiap sesi; berbayar: sekali seumur akaun.
            const isTrial = d.tier === 'trial';
            setTutorialIsTrial(isTrial);
            if (isTrial) {
                let alreadyThisSession = false;
                try { alreadyThisSession = sessionStorage.getItem('tutorialShownThisSession') === '1'; } catch (_) { }
                if (!alreadyThisSession) setShowTutorial(true);
            } else if (d.tutorialSeen !== true) {
                setShowTutorial(true);
            }

            // Group ejen yang merujuk — kalau tiada, group rasmi digunakan (lihat supportLink).
            if (!d.referredBy) return;
            try {
                const aff = await getAffiliateBySlug(d.referredBy);
                if (!cancelled && aff?.supportGroupLink) setAffiliateGroupLink(aff.supportGroupLink);
            } catch (_) { }
        })();

        return () => { cancelled = true; };
    }, [enabled]);

    /* Keutamaan: (1) group ejen yang merujuk, kalau ada; (2) group rasmi
       SESAAT ikut tier — Free trial vs Premium/Fighter. Dikira semula bila
       tier berubah, sebab `isPaid` selalunya sampai selepas render pertama. */
    const supportLink = useMemo(
        () => affiliateGroupLink || officialSupportGroup(isPaid),
        [affiliateGroupLink, isPaid],
    );

    /* ══ Produk terbaru ══
       10, bukan 6 — seksyen ini lebar penuh, jadi 6 kad terpaksa regang
       sampai besar. Bilangan lajur (2/5/10) semuanya pembahagi 10, jadi
       tiada baris terakhir yang tergantung separuh pada mana-mana skrin. */
    useEffect(() => {
        if (!enabled || !showNewProducts) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const q = query(
                    collection(db, 'library', 'product', 'items'),
                    where('group', '==', 'system'),
                    where('hasIntelligence', '==', true),
                    orderBy('addedAt', 'desc'),
                    limit(10),
                );
                const snap = await getDocs(q);
                if (cancelled) return;
                setLatestProducts(snap.docs.map((x) => ({ id: x.id, ...x.data() })));
            } catch (err) {
                console.error('[Dashboard] Gagal muat produk terbaru:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [enabled, showNewProducts]);

    /* ── Tindakan wish ──────────────────────────────────────── */
    const startEditWish = useCallback(() => {
        setWishDraft(wish);
        setIsEditingWish(true);
        setTimeout(() => wishInputRef.current?.select(), 50);
    }, [wish]);

    const cancelEditWish = useCallback(() => setIsEditingWish(false), []);

    const saveWish = useCallback(async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const trimmed = wishDraft.trim() || DEFAULT_WISH;
        setSavingWish(true);
        try {
            await setDoc(doc(db, 'users', uid), { dashboardWish: trimmed }, { merge: true });
            setWish(trimmed);
            setIsEditingWish(false);
        } finally {
            setSavingWish(false); // kekal dalam mod edit kalau simpan gagal — teks pengguna tidak hilang
        }
    }, [wishDraft]);

    const onWishKeyDown = useCallback((e) => {
        if (e.key === 'Enter') saveWish();
        if (e.key === 'Escape') cancelEditWish();
    }, [saveWish, cancelEditWish]);

    /* ── Tutup tutorial ─────────────────────────────────────── */
    const closeTutorial = useCallback(async () => {
        setShowTutorial(false);
        if (tutorialIsTrial) {
            try { sessionStorage.setItem('tutorialShownThisSession', '1'); } catch (_) { }
            return;
        }
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        try { await setDoc(doc(db, 'users', uid), { tutorialSeen: true }, { merge: true }); } catch (_) { }
    }, [tutorialIsTrial]);

    /* ── Nilai terbitan ─────────────────────────────────────── */
    const displayName = user?.displayName || user?.email?.split('@')[0] || 'Pengguna';

    /* ★ Akaun Google-sahaja (tiada provider 'password') → tak boleh log masuk di
       browser lain tanpa lalui tetingkap Google. Ajak set kata laluan; notis
       hilang sendiri sebaik provider 'password' wujud (dikira setiap render). */
    const authProviders = (auth.currentUser?.providerData || []).map((p) => p.providerId);
    const needsPasswordSetup = authProviders.includes('google.com') && !authProviders.includes('password');

    return {
        displayName,
        greeting: greetingNow(),
        greetingKey: greetingKeyNow(),
        needsPasswordSetup,

        wish,
        isEditingWish,
        wishDraft,
        setWishDraft,
        savingWish,
        wishInputRef,
        startEditWish,
        saveWish,
        cancelEditWish,
        onWishKeyDown,

        supportLink,
        latestProducts,

        showTutorial,
        tutorialIsTrial,
        closeTutorial,
    };
}
